import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  doc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  db,
  getWatchBrands,
  getExistingReferenceMapById,
  getCustomCalibres,
  saveCustomCalibre,
  enrichCalibreWithAI,
  type CustomCalibreEntry,
  type CalibreAliasDetail,
} from '../../services/firebase';
import {
  calibreCanonicalMatchKey,
  calibreMatchKeysForBrand,
  calibreRefMatchMethod,
  duplicateAliasMessage,
  findDuplicateAlias,
  normalizeCalibreKey,
  buildCalibreIdIndex,
} from '../../services/calibreResolution';
import { calibreVisibleToBrand } from '../../services/calibreClassification';
import { type CalibreDualWriteFlags } from './calibreBulkUpdate';
import {
  buildParentCalibreFixes,
  buildParentCalibreUpdates,
  buildRefUpdates,
  displayCalibreForRef,
  pushCalibreDataToRefs,
  pushParentCalibresToVariants,
  type ParentCalibreFix,
} from './parentCalibrePush';
// Re-exported: BulkCalibreEnricher.test.ts imports these from here.
export { buildParentCalibreFixes, buildParentCalibreUpdates, buildRefUpdates };
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import type { WatchBrand, WatchReference } from '../../types';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { BrandFilterSelect } from './BrandFilterSelect';
import {
  AiCalibreEnrichPanel,
  aiSpecsToCalibreData,
  proposedTiersToCalibreTiers,
  resolveBrandManufacturerId,
  resultToDraft,
  type AiEnrichState,
  type EditableResult,
} from './AiCalibreEnrichPanel';

const CUSTOM_CALIBRES_COLLECTION = 'custom_calibres';

type SectionKey = 'unmatched' | 'missing' | 'matched';

export interface CalibreMatch {
  entry: CustomCalibreEntry;
  matchType: 'name' | 'alias' | 'link';
  matchedOn: string;
}

interface MissingRow {
  refId: string;
  reference: string;
  model?: string;
}


interface UnmatchedGroup {
  calibreValue: string;
  refs: WatchReference[];
}

interface MatchedGroup {
  calibreValue: string;
  refs: WatchReference[];
  matchedEntry: CustomCalibreEntry;
  matchType: 'name' | 'alias' | 'link';
  /** The un-normalized name or alias the ref's calibre value matched against. */
  matchedOn: string;
}

interface ScanResults {
  brandId: string;
  brandName: string;
  matched: MatchedGroup[];
  missing: MissingRow[];
  unmatched: UnmatchedGroup[];
  /** The subset of `missing` a parent can fill in. Computed by the same scan —
   *  no extra reads, since the parents are already in the fetched map. */
  parentFixes: ParentCalibreFix[];
}

type ActiveModal = { calibre: string; type: 'ai' | 'aka' } | null;

interface ApplyPreview {
  groups: number;
  refsWithUpdates: number;
  fieldsToWrite: number;
  totalRefs: number;
  /** Per-group breakdown for display. */
  perGroup: {
    calibreValue: string;
    matchedName: string;
    refsWithUpdates: number;
    fieldsToWrite: number;
    totalRefs: number;
  }[];
}

interface ApplyProgress {
  current: number;
  total: number;
  currentLabel?: string;
}

import { GENERIC_MOVEMENT_MAKERS } from './CalibreActionToolbar';

/** Shared calibre comparison key (trim, collapse, lower, caliber→calibre fold). */
const normalizeKey = normalizeCalibreKey;

/**
 * Build a lookup index from custom calibres: normalized name/alias → match info.
 *
 * Two passes, the same pair the bulk importer runs, so the two screens agree on
 * what a brand's references may match:
 *  1. ALIASES, brand-scoped. {@link calibreVisibleToBrand} — are this calibre's
 *     house nicknames on offer to the brand at all? Judged over its FULL brand
 *     set (primary `brandId` plus every brand-scoped alias). The old gate here
 *     read the primary `brandId` alone, so "Miyota Calibre 2035" was discarded
 *     before its Bulova-scoped `2035` alias was ever read.
 *     {@link calibreMatchKeysForBrand} then says which of its names this brand
 *     may match it by: it still refuses an alias scoped to another brand, and it
 *     walks `aliasDetails` directly rather than through the flat `alsoKnownAs`
 *     mirror, so an alias missing from that mirror is no longer invisible.
 *  2. CANONICAL NAMES, unscoped — {@link calibreCanonicalMatchKey}. A calibre's
 *     own name matches a reference from any brand, because brands fit base
 *     movements they did not make: a Bulova reference reading "Sellita Calibre
 *     SW200" should find the Sellita-owned calibre of that exact name.
 *
 * Two deliberate narrowings come with the shared helpers: catch-all calibres and
 * placeholder strings ("Quartz", "Unknown") stop matching here, as they already
 * did in the importer.
 *
 * A canonical-name match outranks an alias on the same key, as before.
 */
export function buildBrandScopedIndex(
  allCalibres: CustomCalibreEntry[],
  brand: WatchBrand,
): Map<string, CalibreMatch> {
  const idx = new Map<string, CalibreMatch>();
  const record = (entry: CustomCalibreEntry, key: string) => {
    const method = calibreRefMatchMethod(entry, key, brand.id);
    if (!method) return;
    const matchType = method.via === 'canonical' ? 'name' : 'alias';
    if (matchType === 'alias' && idx.get(key)?.matchType === 'name') return;
    idx.set(key, { entry, matchType, matchedOn: method.matchedName });
  };

  for (const entry of allCalibres) {
    // Legacy free-text `brandName` on the calibre may hold either spelling of
    // the brand, so ask under both — the id path is identical either way.
    const visible =
      calibreVisibleToBrand(entry, brand.id, brand.name) ||
      calibreVisibleToBrand(entry, brand.id, brand.displayName);
    if (!visible) continue;
    for (const key of calibreMatchKeysForBrand(entry, brand.id)) record(entry, key);
  }
  for (const entry of allCalibres) {
    const canonical = calibreCanonicalMatchKey(entry);
    if (canonical) record(entry, canonical);
  }
  return idx;
}


/**
 * Which calibre one reference belongs to, by the scan's own rule: a `calibreId`
 * that resolves wins outright (whatever the text says); otherwise the text is
 * looked up in the brand's index. `raw` is empty for a "missing" reference.
 *
 * Exported so the calibre-side "Reapply to matching references" tool decides
 * with the very function this scan uses, rather than a copy of it.
 */
export function matchRefForBrand(
  ref: WatchReference,
  calibresById: ReadonlyMap<string, CustomCalibreEntry>,
  index: ReadonlyMap<string, CalibreMatch>,
): { raw: string; key: string; hit: CalibreMatch | undefined } {
  const linked = ref.calibreId ? calibresById.get(ref.calibreId.trim()) : undefined;
  const raw = displayCalibreForRef(ref, calibresById);
  if (!raw) return { raw, key: '', hit: undefined };
  const key = normalizeKey(raw);
  const hit: CalibreMatch | undefined = linked
    ? { entry: linked, matchType: 'link', matchedOn: linked.name }
    : index.get(key);
  return { raw, key, hit };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface BulkCalibreEnricherProps {
  /** Pre-select this brand on mount (deep-link from the Enrichment Tasks panel). */
  initialBrandId?: string;
  /** Called once the initial brand has been applied, so the parent can clear it. */
  onBrandConsumed?: () => void;
  /** When true the tool is hosted inside the per-brand panel's modal: hide the
   *  page header and brand picker (brand is locked via initialBrandId). */
  embedded?: boolean;
}

export function BulkCalibreEnricher({
  initialBrandId,
  onBrandConsumed,
  embedded = false,
}: BulkCalibreEnricherProps = {}) {
  // Calibre write-path migration: both default ON, so every payload below is
  // unchanged until the cutover deliberately retires a legacy field. Same pair
  // the References bulk action bar reads.
  const dualWriteLegacyMovement = useFeatureFlag('calibre.dualWriteLegacyMovement');
  const dualWriteLegacyCalibreString = useFeatureFlag(
    'calibre.dualWriteLegacyCalibreString',
  );
  const flags: CalibreDualWriteFlags = useMemo(
    () => ({
      legacyMovement: dualWriteLegacyMovement,
      legacyCalibreString: dualWriteLegacyCalibreString,
    }),
    [dualWriteLegacyMovement, dualWriteLegacyCalibreString],
  );

  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');

  // Seed the brand picker once when deep-linked from the per-brand panel.
  useEffect(() => {
    if (initialBrandId) {
      setSelectedBrandId(initialBrandId);
      onBrandConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBrandId]);
  const [allCalibres, setAllCalibres] = useState<CustomCalibreEntry[]>([]);
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState<ScanResults | null>(null);
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({
    unmatched: true,
    missing: false,
    matched: false,
  });
  const [creating, setCreating] = useState<Record<string, boolean>>({});
  const [bulkCreating, setBulkCreating] = useState(false);
  const [aiState, setAiState] = useState<Record<string, AiEnrichState>>({});
  // Per-calibre "Reference info" free text — preserved across re-runs (kept
  // separate from aiState so re-running, which resets aiState[key], won't wipe it).
  const [refInfo, setRefInfo] = useState<Record<string, string>>({});
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [akaScope, setAkaScope] = useState<'brand' | 'all'>('brand');
  const [akaSearch, setAkaSearch] = useState('');
  const [akaSaving, setAkaSaving] = useState<string | null>(null);
  const [appliedCalibres, setAppliedCalibres] = useState<Set<string>>(new Set());
  const [applyPreview, setApplyPreview] = useState<ApplyPreview | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyProgress, setApplyProgress] = useState<ApplyProgress | null>(null);
  // The parent-calibre Apply runs through the same preview → confirm → batch
  // path, on its own state so neither modal can be open over the other's run.
  const [parentPreview, setParentPreview] = useState<ApplyPreview | null>(null);
  const [applyingParent, setApplyingParent] = useState(false);

  // Load brands once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getWatchBrands();
        if (!cancelled) {
          setBrands(list.sort((a, b) => a.name.localeCompare(b.name)));
        }
      } catch (err) {
        console.error('Failed to load brands', err);
        toast.error('Failed to load brands');
      } finally {
        if (!cancelled) setLoadingBrands(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedBrand = useMemo(
    () => brands.find((b) => b.id === selectedBrandId),
    [brands, selectedBrandId],
  );

  /** Reset all scan-derived state — used when the brand changes or before a new scan. */
  const resetScanState = useCallback(() => {
    setResults(null);
    setAiState({});
    setCreating({});
    setActiveModal(null);
    setAkaScope('brand');
    setAkaSearch('');
    setAkaSaving(null);
    setAppliedCalibres(new Set());
    setApplyPreview(null);
    setApplying(false);
    setApplyProgress(null);
    setParentPreview(null);
    setApplyingParent(false);
  }, []);

  const handleBrandChange = (brandId: string) => {
    setSelectedBrandId(brandId);
    resetScanState();
  };

  const handleScan = useCallback(async () => {
    if (!selectedBrand) {
      toast.error('Select a brand first');
      return;
    }
    setScanning(true);
    resetScanState();
    try {
      // DOC-ID keyed, deliberately. The ref-string-keyed map this used to read
      // collapses every doc sharing a `reference` value into one entry, and a
      // Shopify parent's `reference` IS its lead variant's SKU — so each
      // multi-variant product silently lost one of the two, and the scan
      // reported fewer references than the brand has. Doc ids are unique, and
      // they are also what `parentReferenceId` names, which is what makes the
      // parent lookup below possible at all. Same single read either way.
      const [refMap, calibres] = await Promise.all([
        getExistingReferenceMapById(selectedBrand.id),
        getCustomCalibres(),
      ]);
      setAllCalibres(calibres);
      const refs = Array.from(refMap.values()).sort((a, b) =>
        (a.reference || '').localeCompare(b.reference || ''),
      );

      const index = buildBrandScopedIndex(calibres, selectedBrand);

      const missing: MissingRow[] = [];
      const matchedMap = new Map<string, MatchedGroup>();
      const unmatchedMap = new Map<string, UnmatchedGroup>();

      // The `calibreId` FK identifies a calibre exactly, so a ref carrying one
      // groups under THAT calibre no matter what its legacy string says — or
      // whether it still has one at all. Only refs without a resolvable link
      // fall back to matching on the name.
      const calibresById = buildCalibreIdIndex(calibres);

      for (const ref of refs) {
        const { raw, key, hit } = matchRefForBrand(ref, calibresById, index);
        if (!raw) {
          missing.push({ refId: ref.id, reference: ref.reference, model: ref.model });
          continue;
        }
        if (hit) {
          const existing = matchedMap.get(key);
          if (existing) {
            existing.refs.push(ref);
          } else {
            matchedMap.set(key, {
              calibreValue: raw,
              refs: [ref],
              matchedEntry: hit.entry,
              matchType: hit.matchType,
              matchedOn: hit.matchedOn,
            });
          }
        } else {
          const existing = unmatchedMap.get(key);
          if (existing) {
            existing.refs.push(ref);
          } else {
            unmatchedMap.set(key, { calibreValue: raw, refs: [ref] });
          }
        }
      }

      const sortGroupsByRefCount = <T extends { refs: unknown[] }>(g: T[]): T[] =>
        g.sort((a, b) => b.refs.length - a.refs.length);

      const parentFixes = buildParentCalibreFixes(refMap, calibres, selectedBrand);

      setResults({
        brandId: selectedBrand.id,
        brandName: selectedBrand.name,
        matched: sortGroupsByRefCount(Array.from(matchedMap.values())),
        missing,
        unmatched: sortGroupsByRefCount(Array.from(unmatchedMap.values())),
        parentFixes,
      });

      const matchedRefCount = Array.from(matchedMap.values()).reduce(
        (sum, g) => sum + g.refs.length,
        0,
      );
      toast.success(
        `Scanned ${refs.length} refs — ${matchedRefCount} matched (${matchedMap.size} groups), ${unmatchedMap.size} unmatched groups, ${missing.length} missing (${parentFixes.length} fillable from parent)`,
      );
    } catch (err) {
      console.error('Scan failed', err);
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  }, [selectedBrand, resetScanState]);

  /** Move an unmatched group to the matched section. Does NOT push data to refs
   *  — pushing is a separate user-initiated Apply step. */
  const promoteToMatched = useCallback(
    (
      calibreValue: string,
      matchedEntry: CustomCalibreEntry,
      matchType: 'name' | 'alias' | 'link',
      matchedOn: string,
    ) => {
      const key = normalizeKey(calibreValue);
      setResults((prev) => {
        if (!prev) return prev;
        const group = prev.unmatched.find((g) => normalizeKey(g.calibreValue) === key);
        if (!group) return prev;
        const newGroup: MatchedGroup = {
          calibreValue: group.calibreValue,
          refs: group.refs,
          matchedEntry,
          matchType,
          matchedOn,
        };
        return {
          ...prev,
          unmatched: prev.unmatched.filter((g) => normalizeKey(g.calibreValue) !== key),
          matched: [...prev.matched, newGroup].sort((a, b) => b.refs.length - a.refs.length),
        };
      });
      // Any prior "applied" state for this calibre no longer reflects the new
      // entry's data, so clear it to nudge the user to re-Apply.
      setAppliedCalibres((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    },
    [],
  );

  const handleCreateNew = useCallback(
    async (calibreValue: string) => {
      if (!selectedBrand) return;
      const key = normalizeKey(calibreValue);
      setCreating((p) => ({ ...p, [key]: true }));
      try {
        const docId = await saveCustomCalibre(
          calibreValue.trim(),
          {},
          selectedBrand.name,
          undefined,
          undefined,
          undefined,
          false,
          undefined,
          undefined,
          true, // forceCreate
          undefined, // manufacturerId — unknown for a stub (no AI signal)
          undefined, // aliasDetails
          selectedBrand.id,
        );
        const newEntry: CustomCalibreEntry = {
          id: docId,
          name: calibreValue.trim(),
          brandName: selectedBrand.name,
          brandId: selectedBrand.id,
          data: {},
        };
        setAllCalibres((prev) => [...prev, newEntry]);
        promoteToMatched(calibreValue, newEntry, 'name', newEntry.name);
        toast.success(`Created "${calibreValue.trim()}"`);
      } catch (err) {
        console.error('Create calibre failed', err);
        const msg = err instanceof Error ? err.message : 'Failed to create';
        toast.error(msg);
      } finally {
        setCreating((p) => {
          const next = { ...p };
          delete next[key];
          return next;
        });
      }
    },
    [selectedBrand, promoteToMatched],
  );

  const handleCreateAllUnmatched = useCallback(async () => {
    if (!results || !selectedBrand) return;
    if (results.unmatched.length === 0) return;
    if (!window.confirm(
      `Create ${results.unmatched.length} new calibre entries for "${selectedBrand.name}"? Each will be created as an empty entry (no AI enrichment).`,
    )) return;
    setBulkCreating(true);
    let success = 0;
    let failed = 0;
    const groups = [...results.unmatched];
    for (const group of groups) {
      try {
        const docId = await saveCustomCalibre(
          group.calibreValue.trim(),
          {},
          selectedBrand.name,
          undefined,
          undefined,
          undefined,
          false,
          undefined,
          undefined,
          true,
          undefined, // manufacturerId — unknown for a stub (no AI signal)
          undefined, // aliasDetails
          selectedBrand.id,
        );
        const newEntry: CustomCalibreEntry = {
          id: docId,
          name: group.calibreValue.trim(),
          brandName: selectedBrand.name,
          brandId: selectedBrand.id,
          data: {},
        };
        setAllCalibres((prev) => [...prev, newEntry]);
        promoteToMatched(group.calibreValue, newEntry, 'name', newEntry.name);
        success++;
      } catch (err) {
        console.error('Bulk create failed for', group.calibreValue, err);
        failed++;
      }
    }
    setBulkCreating(false);
    if (failed === 0) {
      toast.success(`Created ${success} calibre${success === 1 ? '' : 's'}`);
    } else {
      toast.error(`Created ${success}, ${failed} failed`);
    }
  }, [results, selectedBrand, promoteToMatched]);

  /** Fetch AI enrichment for a calibre. Stores result + editable draft in aiState[key].
   *  Always forwards the preserved reference info; `existingData` carries user
   *  corrections on a "Re-run with corrections" pass (treated as ground truth). */
  const runAiEnrich = useCallback(
    async (calibreValue: string, opts?: { existingData?: Record<string, unknown> }) => {
      if (!selectedBrand) return;
      const key = normalizeKey(calibreValue);
      setAiState((prev) => ({ ...prev, [key]: { loading: true } }));
      try {
        const result = await enrichCalibreWithAI(calibreValue, {
          brandName: selectedBrand.name,
          referenceInfo: refInfo[key]?.trim() || undefined,
          existingData: opts?.existingData,
        });
        setAiState((prev) => ({ ...prev, [key]: { result, draft: resultToDraft(result) } }));
      } catch (err) {
        console.error('AI enrich failed', err);
        const msg = err instanceof Error ? err.message : 'AI lookup failed';
        setAiState((prev) => ({ ...prev, [key]: { error: msg } }));
      }
    },
    [selectedBrand, refInfo],
  );

  // Open the modal WITHOUT auto-running — the user adds reference info first, then
  // presses "Run enrichment".
  const handleOpenAiModal = useCallback((calibreValue: string) => {
    setActiveModal({ calibre: calibreValue, type: 'ai' });
  }, []);

  const handleRefInfoChange = useCallback((calibreValue: string, value: string) => {
    const key = normalizeKey(calibreValue);
    setRefInfo((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDraftChange = useCallback((calibreValue: string, draft: EditableResult) => {
    const key = normalizeKey(calibreValue);
    setAiState((prev) => {
      const entry = prev[key];
      if (!entry) return prev;
      return { ...prev, [key]: { ...entry, draft } };
    });
  }, []);

  // Re-run, feeding the user's current (edited) field values back as ground truth.
  const handleRerunWithCorrections = useCallback(
    (calibreValue: string) => {
      const key = normalizeKey(calibreValue);
      const draft = aiState[key]?.draft;
      const existingData: Record<string, unknown> = { ...(draft?.specs || {}) };
      if (draft?.canonicalName?.trim()) existingData.canonicalName = draft.canonicalName.trim();
      void runAiEnrich(calibreValue, { existingData });
    },
    [aiState, runAiEnrich],
  );

  const handleOpenAkaModal = useCallback((calibreValue: string) => {
    setActiveModal({ calibre: calibreValue, type: 'aka' });
    setAkaScope('brand');
    // Pre-filled with the value being aliased, same as the CalibreActionToolbar
    // picker — the likely target is named after it, and backspacing broadens.
    setAkaSearch(calibreValue.trim());
    setAkaSaving(null);
  }, []);

  const handleCloseModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const handleAcceptAi = useCallback(
    async (calibreValue: string) => {
      if (!selectedBrand) return;
      const key = normalizeKey(calibreValue);
      const entry = aiState[key];
      if (!entry?.result) return;
      // Save the user's edited draft (falls back to the raw result if absent).
      const draft = entry.draft ?? resultToDraft(entry.result);
      const canonicalName = draft.canonicalName.trim() || entry.result.canonicalName || calibreValue;
      const specs = draft.specs;
      const description = draft.description.trim() ? draft.description.trim() : undefined;
      const data = aiSpecsToCalibreData(specs);
      // Persist AI-proposed tier variants (dropped before this fix): the panel
      // doesn't surface them for review, so accept them wholesale — they're
      // already server-sanitised + base-name-stripped. Mirrors the manual
      // editor's data.tiers merge.
      const proposedTiers = proposedTiersToCalibreTiers(draft.tiers);
      if (proposedTiers.length > 0) data.tiers = proposedTiers;
      const isRename = normalizeKey(canonicalName) !== normalizeKey(calibreValue);
      const manufacturer = ((specs.manufacturer as string) || '').toLowerCase();
      const calNameLower = canonicalName.toLowerCase();
      const isGenericMovement = GENERIC_MOVEMENT_MAKERS.some(
        (m) => manufacturer.includes(m) || calNameLower.startsWith(m),
      );
      // Owning manufacturer = brand only when the calibre is in-house (see
      // resolveBrandManufacturerId). manufacturerId is the FK watchlock derives
      // manufacturerIdAncestors from, which the RN explore Manufacturer screen
      // filters on; generic third-party movements stay unscoped.
      const resolvedManufacturerId = resolveBrandManufacturerId({
        specs,
        brandName: selectedBrand.name,
        brandId: selectedBrand.id,
        isGeneric: isGenericMovement,
      });

      // On a rename of a brand-scoped calibre, brand-scope the demoted old name so
      // a short/ambiguous alias (e.g. "630") only matches refs of this brand and
      // doesn't collide across brands. Generic movements stay unscoped (universal).
      const demotedAliasDetails: CalibreAliasDetail[] | undefined =
        isRename && !isGenericMovement
          ? [{ name: calibreValue.trim(), brandId: selectedBrand.id, showInExplore: false }]
          : undefined;

      setAiState((prev) => ({ ...prev, [key]: { ...entry, saving: true } }));
      try {
        const docId = await saveCustomCalibre(
          canonicalName,
          data,
          isGenericMovement ? undefined : selectedBrand.name,
          isRename ? [calibreValue] : undefined,
          undefined, // images
          undefined, // sourceUrl
          false,     // isEdit
          description,
          undefined, // forceOverwriteId
          undefined, // forceCreate
          resolvedManufacturerId, // manufacturerId
          demotedAliasDetails, // aliasDetails — brand-scoped demoted name
          isGenericMovement ? undefined : selectedBrand.id,
        );
        const newEntry: CustomCalibreEntry = {
          id: docId,
          name: canonicalName,
          brandName: isGenericMovement ? undefined : selectedBrand.name,
          brandId: isGenericMovement ? undefined : selectedBrand.id,
          alsoKnownAs: isRename ? [calibreValue] : undefined,
          aliasDetails: demotedAliasDetails,
          data,
          description,
        };
        setAllCalibres((prev) => [...prev, newEntry]);
        promoteToMatched(
          calibreValue,
          newEntry,
          isRename ? 'alias' : 'name',
          isRename ? calibreValue : canonicalName,
        );
        setAiState((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        setActiveModal(null);
        toast.success(isRename ? `Saved as "${canonicalName}"` : `Saved "${canonicalName}"`);
      } catch (err) {
        console.error('Accept AI failed', err);
        const msg = err instanceof Error ? err.message : 'Save failed';
        setAiState((prev) => ({ ...prev, [key]: { ...entry, saving: false, error: msg } }));
        toast.error(`Failed to save: ${msg}`);
      }
    },
    [selectedBrand, aiState, promoteToMatched],
  );

  const handleAddAsAka = useCallback(
    async (targetEntry: CustomCalibreEntry, calibreValue: string) => {
      if (!selectedBrand) return;
      const key = normalizeKey(calibreValue);
      // Same dedup the calibre editor applies — without it this path appended a
      // second copy of an alias the calibre already carried.
      const duplicate = findDuplicateAlias(targetEntry, calibreValue, selectedBrand.id);
      if (duplicate) {
        toast.error(duplicateAliasMessage(duplicate, calibreValue, targetEntry.name));
        return;
      }
      setAkaSaving(key);
      try {
        const newAliasDetails: CalibreAliasDetail[] = [
          ...(targetEntry.aliasDetails || []),
          { name: calibreValue.trim(), brandId: selectedBrand.id, showInExplore: false },
        ];
        await updateDoc(doc(db, CUSTOM_CALIBRES_COLLECTION, targetEntry.id), {
          alsoKnownAs: arrayUnion(calibreValue.trim()),
          aliasDetails: newAliasDetails,
          updatedAt: serverTimestamp(),
        });
        const updatedEntry: CustomCalibreEntry = {
          ...targetEntry,
          alsoKnownAs: [
            ...(targetEntry.alsoKnownAs || []).filter((a) => normalizeKey(a) !== key),
            calibreValue.trim(),
          ],
          aliasDetails: newAliasDetails,
        };
        setAllCalibres((prev) =>
          prev.map((e) => (e.id === targetEntry.id ? updatedEntry : e)),
        );
        promoteToMatched(calibreValue, updatedEntry, 'alias', calibreValue.trim());
        setActiveModal(null);
        setAkaSearch('');
        toast.success(`Added "${calibreValue}" as alias of "${targetEntry.name}"`);
      } catch (err) {
        console.error('Add as AKA failed', err);
        const msg = err instanceof Error ? err.message : 'Failed to add alias';
        toast.error(msg);
      } finally {
        setAkaSaving(null);
      }
    },
    [selectedBrand, promoteToMatched],
  );

  /** Build a preview without writing — counts how many fields would be set
   *  across how many references for each matched group. Synchronous: local only. */
  const computeApplyPreview = useCallback((): ApplyPreview | null => {
    if (!results || results.matched.length === 0) return null;
    let refsWithUpdates = 0;
    let fieldsToWrite = 0;
    const perGroup: ApplyPreview['perGroup'] = [];
    for (const group of results.matched) {
      let groupRefs = 0;
      let groupFields = 0;
      for (const ref of group.refs) {
        const updates = buildRefUpdates(ref, group.matchedEntry, flags);
        const n = Object.keys(updates).length;
        if (n > 0) {
          groupRefs++;
          groupFields += n;
        }
      }
      refsWithUpdates += groupRefs;
      fieldsToWrite += groupFields;
      perGroup.push({
        calibreValue: group.calibreValue,
        matchedName: group.matchedEntry.name,
        refsWithUpdates: groupRefs,
        fieldsToWrite: groupFields,
        totalRefs: group.refs.length,
      });
    }
    return {
      groups: results.matched.length,
      refsWithUpdates,
      fieldsToWrite,
      totalRefs: results.matched.reduce((s, g) => s + g.refs.length, 0),
      perGroup,
    };
  }, [results, flags]);

  const handleOpenApplyModal = useCallback(() => {
    const preview = computeApplyPreview();
    if (!preview) return;
    setApplyPreview(preview);
  }, [computeApplyPreview]);

  /** The parent-calibre counterpart, grouped ONE ROW PER PARENT so the
   *  breakdown reads as "this product's variants, from this calibre" — the
   *  unit an admin is actually deciding about. Synchronous: local only. */
  const computeParentApplyPreview = useCallback((): ApplyPreview | null => {
    if (!results || results.parentFixes.length === 0) return null;
    const byParent = new Map<string, ApplyPreview['perGroup'][number]>();
    let refsWithUpdates = 0;
    let fieldsToWrite = 0;

    for (const fix of results.parentFixes) {
      const n = Object.keys(buildParentCalibreUpdates(fix, flags)).length;
      const row = byParent.get(fix.parent.id) ?? {
        calibreValue: fix.entry.name,
        matchedName: fix.parent.reference || fix.parent.id,
        refsWithUpdates: 0,
        fieldsToWrite: 0,
        totalRefs: 0,
      };
      row.totalRefs++;
      if (n > 0) {
        row.refsWithUpdates++;
        row.fieldsToWrite += n;
        refsWithUpdates++;
        fieldsToWrite += n;
      }
      byParent.set(fix.parent.id, row);
    }

    return {
      groups: byParent.size,
      refsWithUpdates,
      fieldsToWrite,
      totalRefs: results.parentFixes.length,
      perGroup: Array.from(byParent.values()).sort((a, b) => b.totalRefs - a.totalRefs),
    };
  }, [results, flags]);

  const handleOpenParentModal = useCallback(() => {
    const preview = computeParentApplyPreview();
    if (!preview) return;
    setParentPreview(preview);
  }, [computeParentApplyPreview]);

  const handleCancelParentApply = useCallback(() => {
    if (applyingParent) return; // Don't allow closing mid-apply
    setParentPreview(null);
  }, [applyingParent]);

  const handleConfirmParentApply = useCallback(async () => {
    if (!results || !parentPreview) return;
    const fixes = results.parentFixes;
    setApplyingParent(true);
    setApplyProgress({ current: 0, total: fixes.length });
    const runToken = `bulk-parent-calibre-${crypto.randomUUID()}`;
    try {
      const { updatedCount } = await pushParentCalibresToVariants(
        results.brandId,
        fixes,
        flags,
        runToken,
      );
      setApplyProgress({ current: fixes.length, total: fixes.length });

      // The written variants now HAVE a calibre, so they belong in neither list
      // any more. Dropping them (rather than leaving stale rows behind) makes
      // the button fall to 0 and the Missing count fall by the same number.
      // Their new matched grouping only appears on the next scan, which the
      // toast says outright rather than faking a regrouping here.
      const doneIds = new Set(fixes.map((f) => f.ref.id));
      setResults((prev) =>
        prev
          ? {
              ...prev,
              missing: prev.missing.filter((m) => !doneIds.has(m.refId)),
              parentFixes: [],
            }
          : prev,
      );
      toast.success(
        `Applied parent calibre to ${updatedCount} variant${updatedCount === 1 ? '' : 's'} — re-scan to regroup them`,
      );
    } catch (err) {
      console.error('Apply parent calibre failed', err);
      toast.error('Apply parent calibre failed');
    } finally {
      setApplyingParent(false);
      setApplyProgress(null);
      setParentPreview(null);
    }
  }, [results, parentPreview, flags]);

  const handleCancelApply = useCallback(() => {
    if (applying) return; // Don't allow closing mid-apply
    setApplyPreview(null);
  }, [applying]);

  const handleConfirmApply = useCallback(async () => {
    if (!results || !applyPreview) return;
    setApplying(true);
    setApplyProgress({ current: 0, total: results.matched.length });
    // One token for the whole Apply, across every group it walks — the trigger
    // skips on "this token differs from the one the doc already carried", so a
    // fresh value per run is all it needs. Deliberately not per group: two groups
    // in the same run touch disjoint refs, and a run-wide value keeps the field
    // readable as "written by this Apply".
    const runToken = `bulk-enrich-${crypto.randomUUID()}`;
    let totalUpdated = 0;
    let failed = 0;
    let totalRefs = 0;
    const appliedKeys: string[] = [];
    for (let i = 0; i < results.matched.length; i++) {
      const group = results.matched[i];
      const key = normalizeKey(group.calibreValue);
      setApplyProgress({
        current: i,
        total: results.matched.length,
        currentLabel: group.matchedEntry.name,
      });
      totalRefs += group.refs.length;
      try {
        const { updatedRefs, updatedCount } = await pushCalibreDataToRefs(
          results.brandId,
          group.refs,
          group.matchedEntry,
          flags,
          runToken,
        );
        totalUpdated += updatedCount;
        appliedKeys.push(key);
        // Update cached refs so a subsequent Apply correctly reports 0 work
        setResults((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            matched: prev.matched.map((g) =>
              normalizeKey(g.calibreValue) === key ? { ...g, refs: updatedRefs } : g,
            ),
          };
        });
      } catch (err) {
        console.error('Apply failed for', group.calibreValue, err);
        failed++;
      }
    }
    setApplyProgress({ current: results.matched.length, total: results.matched.length });
    if (appliedKeys.length > 0) {
      setAppliedCalibres((prev) => {
        const next = new Set(prev);
        for (const k of appliedKeys) next.add(k);
        return next;
      });
    }
    setApplying(false);
    setApplyProgress(null);
    setApplyPreview(null);
    if (failed === 0) {
      toast.success(
        `Updated ${totalUpdated} of ${totalRefs} references across ${results.matched.length} calibres`,
      );
    } else {
      toast.error(
        `Updated ${totalUpdated}/${totalRefs}; ${failed} calibre${failed === 1 ? '' : 's'} failed`,
      );
    }
  }, [results, applyPreview, flags]);

  const akaCandidates = useMemo(() => {
    if (activeModal?.type !== 'aka' || !selectedBrand) return [];
    const brandNameLower = selectedBrand.name.toLowerCase();
    const brandDisplayLower = (selectedBrand.displayName || '').toLowerCase();
    // Hard filter when brand-only: ONLY include calibres scoped to the current
    // brand. Prefer canonical brandId; fall back to legacy brandName compared
    // against both brand.name and brand.displayName. Generic/unscoped calibres
    // (no brandId and no brandName) are excluded.
    const scopeFiltered =
      akaScope === 'brand'
        ? allCalibres.filter((c) => {
            if (c.brandId) return c.brandId === selectedBrand.id;
            if (c.brandName) {
              const cb = c.brandName.toLowerCase();
              return cb === brandNameLower || cb === brandDisplayLower;
            }
            return false;
          })
        : allCalibres;
    const term = normalizeKey(akaSearch);
    const filtered = !term
      ? scopeFiltered
      : scopeFiltered.filter((c) => {
          if (normalizeKey(c.name).includes(term)) return true;
          if ((c.alsoKnownAs || []).some((a) => normalizeKey(a).includes(term))) return true;
          return false;
        });
    return filtered
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 100);
  }, [activeModal, allCalibres, akaScope, akaSearch, selectedBrand]);

  const toggleSection = (key: SectionKey) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const activeAiState =
    activeModal?.type === 'ai' ? aiState[normalizeKey(activeModal.calibre)] : undefined;

  const matchedRefTotal = useMemo(
    () => results?.matched.reduce((sum, g) => sum + g.refs.length, 0) ?? 0,
    [results],
  );

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calibre Enrichment</h1>
          <p className="text-gray-500 mt-1">
            Scan a brand's references and reconcile their{' '}
            <code className="font-mono text-sm">calibre</code> values against the{' '}
            <code className="font-mono text-sm">custom_calibres</code> database. Create new
            entries, add aliases, enrich via AI — and push calibre data back to references.
          </p>
        </div>
      )}

      {/* Brand picker (hidden when brand is locked) */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        {!embedded && (
          <BrandFilterSelect
            brands={brands}
            loading={loadingBrands}
            value={selectedBrandId}
            onChange={handleBrandChange}
          />
        )}
        <div>
          <Button onClick={handleScan} disabled={!selectedBrandId || scanning} loading={scanning}>
            Scan references
          </Button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {results.brandName}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                <span className="text-green-700 font-medium">
                  {matchedRefTotal} matched ({results.matched.length} groups)
                </span>
                {' · '}
                <span className="text-amber-700 font-medium">{results.unmatched.length} unmatched groups</span>
                {' · '}
                <span className="text-gray-600 font-medium">{results.missing.length} missing</span>
                {results.parentFixes.length > 0 && (
                  <>
                    {' · '}
                    <span className="text-blue-700 font-medium">
                      {results.parentFixes.length} fillable from parent
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={handleOpenApplyModal}
                disabled={results.matched.length === 0 || applying}
                variant="success"
              >
                Apply Calibre Data to References ({results.matched.length} group
                {results.matched.length === 1 ? '' : 's'}, {matchedRefTotal} ref
                {matchedRefTotal === 1 ? '' : 's'})
              </Button>
              <Button
                onClick={handleOpenParentModal}
                disabled={results.parentFixes.length === 0 || applyingParent}
                variant="secondary"
                title="Fill each variant that has no calibre from its parent reference"
              >
                Apply Parent Calibre to Variants ({results.parentFixes.length})
              </Button>
              <Button
                onClick={handleCreateAllUnmatched}
                disabled={results.unmatched.length === 0 || bulkCreating}
                loading={bulkCreating}
                variant="secondary"
                size="sm"
              >
                Create All Unmatched as New ({results.unmatched.length})
              </Button>
            </div>
          </div>

          {/* Unmatched */}
          <Section
            title="Unmatched"
            count={results.unmatched.length}
            expanded={expanded.unmatched}
            onToggle={() => toggleSection('unmatched')}
            tone="amber"
          >
            {results.unmatched.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 italic">Nothing unmatched.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-amber-50 text-xs text-amber-900 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Calibre Value</th>
                    <th className="px-3 py-2 text-left">References</th>
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.unmatched.map((group) => {
                    const key = normalizeKey(group.calibreValue);
                    const isCreating = !!creating[key];
                    return (
                      <tr key={key} className="bg-amber-50/30">
                        <td className="px-3 py-2 align-top">
                          <div className="font-mono text-sm font-medium text-gray-900">
                            {group.calibreValue}
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <RefsDisclosure refs={group.refs} />
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleCreateNew(group.calibreValue)}
                              disabled={isCreating || bulkCreating}
                              loading={isCreating}
                            >
                              Create New
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleOpenAiModal(group.calibreValue)}
                            >
                              AI Enrich
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenAkaModal(group.calibreValue)}
                            >
                              Add as AKA
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Section>

          {/* Missing */}
          <Section
            title="Missing (no calibre value)"
            count={results.missing.length}
            expanded={expanded.missing}
            onToggle={() => toggleSection('missing')}
            tone="gray"
          >
            {results.missing.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 italic">All references have a calibre value.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Reference</th>
                    <th className="px-3 py-2 text-left">Model</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.missing.map((r) => (
                    <tr key={r.refId}>
                      <td className="px-3 py-2 font-mono text-xs">{r.reference}</td>
                      <td className="px-3 py-2">{r.model || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Matched */}
          <Section
            title="Matched"
            count={results.matched.length}
            expanded={expanded.matched}
            onToggle={() => toggleSection('matched')}
            tone="green"
          >
            {results.matched.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 italic">Nothing matched yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-green-50 text-xs text-green-900 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Calibre Value</th>
                    <th className="px-3 py-2 text-left">Matched To</th>
                    <th className="px-3 py-2 text-left">How</th>
                    <th className="px-3 py-2 text-left">References</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.matched.map((group) => {
                    const key = normalizeKey(group.calibreValue);
                    const isApplied = appliedCalibres.has(key);
                    return (
                      <tr key={key} className={isApplied ? 'bg-green-50/30' : ''}>
                        <td className="px-3 py-2 align-top font-mono text-xs">
                          {group.calibreValue}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <span className="text-green-800 font-medium">
                            {group.matchedEntry.name}
                          </span>
                          {group.matchedEntry.brandName && (
                            <span className="text-xs text-gray-500">
                              {' '}
                              ({group.matchedEntry.brandName})
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {group.matchType === 'link' ? (
                            <span
                              className="inline-block px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800 font-medium"
                              title="Matched on the reference's calibreId link, not its name"
                            >
                              link
                            </span>
                          ) : group.matchType === 'name' ? (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800 font-medium">
                              name
                            </span>
                          ) : (
                            <span className="inline-flex items-baseline gap-1">
                              <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800 font-medium">
                                alias
                              </span>
                              <span className="font-mono text-xs text-gray-600">
                                "{group.matchedOn}"
                              </span>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <RefsDisclosure refs={group.refs} />
                        </td>
                        <td className="px-3 py-2 align-top">
                          {isApplied ? (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800 font-medium">
                              ✓ Applied
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">
                              Pending apply
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      )}

      {/* AI Enrich Modal */}
      <Modal
        isOpen={activeModal?.type === 'ai'}
        onClose={handleCloseModal}
        title={activeModal?.type === 'ai' ? `AI Enrich: ${activeModal.calibre}` : ''}
        size="lg"
      >
        {activeModal?.type === 'ai' && (
          <AiCalibreEnrichPanel
            state={activeAiState}
            referenceInfo={refInfo[normalizeKey(activeModal.calibre)] || ''}
            onReferenceInfoChange={(v) => handleRefInfoChange(activeModal.calibre, v)}
            onRun={() => runAiEnrich(activeModal.calibre)}
            onDraftChange={(d) => handleDraftChange(activeModal.calibre, d)}
            onAccept={() => handleAcceptAi(activeModal.calibre)}
            onRerun={() => runAiEnrich(activeModal.calibre)}
            onRerunWithCorrections={() => handleRerunWithCorrections(activeModal.calibre)}
            onClose={handleCloseModal}
          />
        )}
      </Modal>

      {/* Add as AKA Modal */}
      <Modal
        isOpen={activeModal?.type === 'aka'}
        onClose={handleCloseModal}
        title={activeModal?.type === 'aka' ? `Add as Alias: ${activeModal.calibre}` : ''}
        size="lg"
      >
        {activeModal?.type === 'aka' && (
          <AkaPicker
            calibreValue={activeModal.calibre}
            scope={akaScope}
            onScopeChange={setAkaScope}
            search={akaSearch}
            onSearchChange={setAkaSearch}
            candidates={akaCandidates}
            onPick={(entry) => handleAddAsAka(entry, activeModal.calibre)}
            saving={akaSaving === normalizeKey(activeModal.calibre)}
          />
        )}
      </Modal>

      {/* Apply Calibre Data Modal */}
      <Modal
        isOpen={!!applyPreview}
        onClose={handleCancelApply}
        title="Apply Calibre Data to References"
        size="lg"
      >
        {applyPreview && (
          <ApplyPreviewPanel
            preview={applyPreview}
            applying={applying}
            progress={applyProgress}
            onConfirm={handleConfirmApply}
            onCancel={handleCancelApply}
          />
        )}
      </Modal>

      {/* Apply Parent Calibre Modal */}
      <Modal
        isOpen={!!parentPreview}
        onClose={handleCancelParentApply}
        title="Apply Parent Calibre to Variants"
        size="lg"
      >
        {parentPreview && (
          <ApplyPreviewPanel
            mode="parent"
            preview={parentPreview}
            applying={applyingParent}
            progress={applyProgress}
            onConfirm={handleConfirmParentApply}
            onCancel={handleCancelParentApply}
          />
        )}
      </Modal>
    </div>
  );
}

function RefsDisclosure({ refs }: { refs: WatchReference[] }) {
  return (
    <details>
      <summary className="text-xs text-gray-700 cursor-pointer">
        {refs.length} reference{refs.length === 1 ? '' : 's'}
      </summary>
      <ul className="mt-1 text-xs text-gray-600 max-h-40 overflow-y-auto">
        {refs.slice(0, 50).map((r) => (
          <li key={r.id} className="font-mono">
            {r.reference}
            {r.model && <span className="font-sans text-gray-500"> — {r.model}</span>}
          </li>
        ))}
        {refs.length > 50 && (
          <li className="italic text-gray-400">…and {refs.length - 50} more</li>
        )}
      </ul>
    </details>
  );
}

function Section({
  title,
  count,
  expanded,
  onToggle,
  tone,
  children,
}: {
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  tone: 'amber' | 'gray' | 'green';
  children: React.ReactNode;
}) {
  const toneClass = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    gray: 'bg-gray-50 border-gray-200 text-gray-800',
    green: 'bg-green-50 border-green-200 text-green-900',
  }[tone];
  return (
    <div className="border-t border-gray-200">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full px-4 py-3 flex items-center justify-between text-sm font-medium ${toneClass} border-l-4`}
      >
        <span>
          {title} <span className="ml-1 font-normal">({count})</span>
        </span>
        <span className="text-xs">{expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && <div className="overflow-x-auto">{children}</div>}
    </div>
  );
}

function AkaPicker({
  calibreValue,
  scope,
  onScopeChange,
  search,
  onSearchChange,
  candidates,
  onPick,
  saving,
}: {
  calibreValue: string;
  scope: 'brand' | 'all';
  onScopeChange: (s: 'brand' | 'all') => void;
  search: string;
  onSearchChange: (s: string) => void;
  candidates: CustomCalibreEntry[];
  onPick: (entry: CustomCalibreEntry) => void;
  saving: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-700">
        Pick the calibre to attach <span className="font-mono font-medium">{calibreValue}</span>{' '}
        to as an alias.
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => onScopeChange('brand')}
            className={`px-3 py-1.5 ${
              scope === 'brand' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Current brand only
          </button>
          <button
            type="button"
            onClick={() => onScopeChange('all')}
            className={`px-3 py-1.5 border-l border-gray-300 ${
              scope === 'all' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            All brands
          </button>
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search calibres…"
          className="flex-1 min-w-[200px] border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
        />
      </div>
      <div className="max-h-[50vh] overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
        {candidates.length === 0 ? (
          <div className="p-3 text-sm text-gray-500 italic">No calibres match.</div>
        ) : (
          candidates.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onPick(c)}
              disabled={saving}
              className="w-full text-left p-2 hover:bg-brand-50 disabled:opacity-50 text-sm flex items-baseline gap-2"
            >
              <span className="font-mono font-medium">{c.name}</span>
              {c.brandName && (
                <span className="text-gray-500 text-xs">({c.brandName})</span>
              )}
              {c.data?.movementType && (
                <span className="text-gray-400 text-xs">— {c.data.movementType}</span>
              )}
              {c.alsoKnownAs && c.alsoKnownAs.length > 0 && (
                <span className="text-gray-400 text-xs truncate">
                  aka: {c.alsoKnownAs.slice(0, 3).join(', ')}
                  {c.alsoKnownAs.length > 3 ? '…' : ''}
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * Shared confirm-and-progress panel for both Apply paths.
 *
 * `mode` swaps the wording and the two group-column labels only — the counts,
 * the progress bar and the confirm/cancel wiring are identical, because the two
 * runs genuinely do the same thing (build a payload per reference, batch it)
 * and differ solely in how the references were chosen.
 */
function ApplyPreviewPanel({
  preview,
  applying,
  progress,
  onConfirm,
  onCancel,
  mode = 'matched',
}: {
  preview: ApplyPreview;
  applying: boolean;
  progress: ApplyProgress | null;
  onConfirm: () => void;
  onCancel: () => void;
  mode?: 'matched' | 'parent';
}) {
  const isParent = mode === 'parent';
  if (applying && progress) {
    const pct = progress.total === 0 ? 0 : Math.round((progress.current / progress.total) * 100);
    return (
      <div className="space-y-3 text-sm">
        <div className="text-gray-700">
          {isParent
            ? 'Applying parent calibres to variants…'
            : 'Applying calibre data to references…'}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className="bg-green-600 h-2 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="text-xs text-gray-600">
          {progress.current} / {progress.total} {isParent ? 'variants' : 'calibres'}
          {progress.currentLabel && (
            <span className="ml-2 text-gray-500">— current: {progress.currentLabel}</span>
          )}
        </div>
        <div className="text-xs text-gray-400 italic">
          Don&apos;t close this dialog until the apply completes.
        </div>
      </div>
    );
  }

  const willWriteAnything = preview.fieldsToWrite > 0;
  return (
    <div className="space-y-3 text-sm">
      <div className="text-gray-700">
        {isParent ? (
          <>
            About to copy each parent reference&apos;s calibre — and its tier, where the
            parent has one — onto its own variants that carry no calibre at all. Only
            variants linked by <code className="font-mono text-xs">parentReferenceId</code>{' '}
            are touched, and only where the parent&apos;s calibre resolves. Nothing that
            already has a calibre is changed.
          </>
        ) : (
          <>
            About to push calibre data into every reference whose calibre value matched a{' '}
            <code className="font-mono text-xs">custom_calibres</code> entry. Empty fields are
            filled. Movement type, calibre name, base calibre name, boolean flags (chronograph,
            GMT, hacking, etc.), and the functions list mirror the calibre exactly — including
            unsetting flags or removing entries the calibre no longer specifies. Other
            hand-entered values are preserved.
          </>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <Stat label={isParent ? 'Parent references' : 'Matched groups'} value={preview.groups} />
        <Stat
          label={isParent ? 'Variants to update' : 'References to update'}
          value={`${preview.refsWithUpdates} / ${preview.totalRefs}`}
        />
        <Stat label="Field writes" value={preview.fieldsToWrite} />
      </div>
      {preview.perGroup.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs text-gray-600">
            {isParent ? 'Per-parent breakdown' : 'Per-group breakdown'}
          </summary>
          <div className="mt-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600 uppercase">
                <tr>
                  <th className="px-2 py-1 text-left">
                    {isParent ? 'Parent Calibre' : 'Calibre Value'}
                  </th>
                  <th className="px-2 py-1 text-left">
                    {isParent ? 'Parent Ref' : 'Matched'}
                  </th>
                  <th className="px-2 py-1 text-right">
                    {isParent ? 'Variants' : 'Refs'}
                  </th>
                  <th className="px-2 py-1 text-right">Fields</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {preview.perGroup.map((g) => (
                  // Composite key: in parent mode the rows are one per PARENT,
                  // and several parents legitimately share one calibre name
                  // (every Heinrich Radiance product runs the same ETA 7001),
                  // so `calibreValue` alone is not unique here.
                  <tr
                    key={`${g.calibreValue}::${g.matchedName}`}
                    className={g.fieldsToWrite === 0 ? 'text-gray-400' : ''}
                  >
                    <td className="px-2 py-1 font-mono">{g.calibreValue}</td>
                    <td className="px-2 py-1">{g.matchedName}</td>
                    <td className="px-2 py-1 text-right">
                      {g.refsWithUpdates} / {g.totalRefs}
                    </td>
                    <td className="px-2 py-1 text-right">{g.fieldsToWrite}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
      {!willWriteAnything && (
        <div className="text-xs text-gray-500 italic">
          No empty fields to fill — all matched references already have the calibre data
          this would push.
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="success" onClick={onConfirm} disabled={!willWriteAnything}>
          Apply ({preview.fieldsToWrite} field{preview.fieldsToWrite === 1 ? '' : 's'})
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-2">
      <div className="text-lg font-semibold text-gray-900">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}
